import { Space, Button, Table, Tag, Tooltip } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import moment from "moment";

import CustomPagination from "../../../../../core/common/customPagination/CustomPagination";
import { WORKFLOW_TRIGGER_TYPE } from "../../../workflow/utils/enums";

import type { ColumnsType } from "antd/es/table";
import type { WorkflowData } from "../../types/automation.types";


interface AutomationListTableProps {
  workflows: WorkflowData[];
  loading: boolean;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onEdit: (record: WorkflowData) => void;
  onHistory: (record: WorkflowData) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (id: number, isActive: boolean) => void;
  onRunManually: (id: number) => void;
}

const AutomationListTable: React.FC<AutomationListTableProps> = ({
  workflows,
  loading,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  onEdit,
  onHistory,
  onDelete,
  onToggleStatus,
  onRunManually,
}) => {
  const totalPages = total === 0 ? 1 : Math.max(1, Math.ceil(total / limit));
  const start = total === 0 ? 0 : (page - 1) * limit;
  const paged = workflows.slice(start, start + limit);

  const columns: ColumnsType<WorkflowData> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Workflow Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 260,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div>
          <div className="fw-semibold" style={{ fontSize: 13.5, color: "#0f172a" }}>
            {text}
          </div>
          {record.description && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Execution Type",
      dataIndex: "execution_type",
      key: "execution_type",
      width: 150,
      render: (text) => <Tag color="blue">{text?.toUpperCase()}</Tag>,
    },
    {
      title: "Trigger Type",
      dataIndex: "trigger_type",
      key: "trigger_type",
      width: 150,
      render: (text) => <Tag color="green">{text?.toUpperCase()}</Tag>,
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 100,
      align: "center",
      render: (version) => <span className="badge bg-secondary">v{version}</span>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 130,
      render: (isActive, record) => (
        <Tooltip title="Click to toggle status">
          {record?.execution_type == WORKFLOW_TRIGGER_TYPE.MANUAL ? (
            <Tag
              color={isActive ? "success" : "default"}
              style={{ cursor: "pointer" }}
              onClick={() => onRunManually(record.id)}
            >
              <PlayCircleOutlined /> RUN
            </Tag>
          ) : (
            <Tag
              color={isActive ? "success" : "default"}
              style={{ cursor: "pointer" }}
              onClick={() => onToggleStatus(record.id, isActive)}
            >
              {isActive ? (
                <>
                  <PlayCircleOutlined /> Active
                </>
              ) : (
                <>
                  <PauseCircleOutlined /> Inactive
                </>
              )}
            </Tag>
          )}
        </Tooltip>
      ),
    },
    {
      title: "Last Executed",
      dataIndex: "last_executed_at",
      key: "last_executed_at",
      width: 150,
      sorter: (a, b) => {
        if (!a.last_executed_at) return 1;
        if (!b.last_executed_at) return -1;
        return (
          new Date(a.last_executed_at).getTime() -
          new Date(b.last_executed_at).getTime()
        );
      },
      render: (date) =>
        date ? (
          <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
            {moment(date).fromNow()}
          </Tooltip>
        ) : (
          <span className="text-muted">Never</span>
        ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date) => (
        <Tooltip title={moment(date).format("YYYY-MM-DD HH:mm:ss")}>
          {moment(date).format("MMM DD, YYYY")}
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Workflow">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            >

            </Button>
          </Tooltip>
          <Tooltip title="View History">
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => onHistory(record)}
            >

            </Button>
          </Tooltip>
          <Tooltip title="Delete Workflow">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record.id)}
            >

            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading automations...</p>
          </div>
        </div>
      ) : total > 0 ? (
        <div
          className="bg-white"
          style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
        >
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={paged}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: 1300 }}
            rowClassName={(_, index) =>
              index % 2 === 0
                ? "data-table-row-light"
                : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            hasNextPage={page * limit < total}
            hasPrevPage={page > 1}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <i className="ti ti-bolt-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
            <h5 className="mt-3 mb-2 fw-semibold">No automations found</h5>
            <p className="text-muted mb-0">Try adjusting your filters or create a new automation.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationListTable;
